// M16 KPI — gösterge kodu üretimi (`kpi_metrics.code`) ve eşleştirme hafızası etiket
// normalizasyonu (`kpi_mapping_memory.normalized_label`, K19). Saf, platform-bağımsız.

export const KPI_CODE_MAX_LENGTH = 64;

const TR_MAP: Record<string, string> = {
  ç: "c",
  Ç: "c",
  ğ: "g",
  Ğ: "g",
  ı: "i",
  I: "i",
  İ: "i",
  i: "i",
  ö: "o",
  Ö: "o",
  ş: "s",
  Ş: "s",
  ü: "u",
  Ü: "u",
};

/** Türkçe harfleri ASCII'ye katlar ve küçük harfe indirir (İ→i, I→i — kod/arama bağlamı). */
function foldTurkish(input: string): string {
  let out = "";
  for (const ch of input) {
    const mapped = TR_MAP[ch];
    out += mapped ?? ch.toLowerCase();
  }
  // Kalan diakritikler (é, â …) NFD ayrıştırma + birleşik işaret atımı.
  return out.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/**
 * Etiketten gösterge kodu: TR çeviri → küçük harf → [a-z0-9] dışı diziler `_` → baş/son `_`
 * kırpılır → harfle başlamalı (rakamla başlarsa `k_` öneki) → ≤64 karakter (sondaki `_`
 * yeniden kırpılır) → boş kalırsa `k`. `existing` verilirse çakışmada `_2`, `_3` … eklenir;
 * ek dahil 64 sınırı korunur (gövde kısaltılır).
 */
export function slugifyKpiCode(label: string, existing?: ReadonlySet<string>): string {
  let code = foldTurkish(label)
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (/^\d/.test(code)) code = `k_${code}`;
  code = truncateCode(code, KPI_CODE_MAX_LENGTH);
  if (code === "") code = "k";
  if (!existing?.has(code)) return code;
  for (let n = 2; ; n += 1) {
    const suffix = `_${n.toString()}`;
    const candidate = truncateCode(code, KPI_CODE_MAX_LENGTH - suffix.length) + suffix;
    if (!existing.has(candidate)) return candidate;
  }
}

function truncateCode(code: string, max: number): string {
  if (code.length <= max) return code;
  return code.slice(0, max).replace(/_+$/, "");
}

// Sondaki birim parantezi "(ton)", "(%)", "(m³)", "(tCO2e)" ya da dipnot "(1)"; ve dipnot
// yıldızı/üst simge rakamı. Sondan tekrar tekrar soyulur ("(tCO2e) (2)" → ikisi de düşer).
const TRAILING_PAREN_RE = /\s*\([^()]{0,24}\)\s*$/u;
const TRAILING_MARKER_RE = /[\s*†‡¹²³⁴⁵⁶⁷⁸⁹⁰]+$/u;

/**
 * Eşleştirme hafızası anahtarı: NFKC → TR katlama + küçük harf → sondaki birim parantezi ve
 * dipnot işaretleri (`*`, `(1)`, üst simge rakam) düşer → boşluk daraltılır. Ortadaki
 * parantez KORUNUR ("Kapsam 1 (doğrudan) emisyonlar").
 */
export function normalizeMappingLabel(raw: string): string {
  let s = foldTurkish(raw.normalize("NFKC"));
  // NFKC üst simge rakamları düz rakama çevirir ("Toplam¹" → "toplam1"); dipnot rakamı
  // kelimeye yapışıksa düşür (asıl sayılar "kapsam 1" gibi boşlukla ayrılır).
  s = s.replace(/(?<=[a-z])\d{1,2}(?=\s*$)/u, "");
  for (;;) {
    const next = s.replace(TRAILING_MARKER_RE, "").replace(TRAILING_PAREN_RE, "");
    if (next === s) break;
    s = next;
  }
  return s.replace(/\s+/g, " ").trim();
}

// ---------------------------------------------------------------------------
// Jenerik etiket (P1-2 — gerçek LLM turu, E8 güçlendirme)
// ---------------------------------------------------------------------------
// "Toplam", "Dizel", "Kadın", "Erkek", "Mavi yaka" gibi bağlamsız satır etiketleri commit'te
// FİRMA GENELİ hafızaya yazılınca sonraki her müşteride 0,9 güvenle yanlış hedefe gidiyordu
// (Lila: "Toplam" ×81 → kapsam1). Jenerik etiket = anlamlı kelime sayısı ≤1; anlamlı kelime =
// stop-list dışı ve en az bir harf içeren jeton (sayı jetonları "kapsam 1" gibi etiketleri
// ayırt ettiği için anlamlı sayılır). Liste `normalizeMappingLabel` çıktısıyla (TR katlanmış,
// küçük harf) karşılaştırılır. BE: `planMemoryWrites`/`lookupMappingMemory` + `kpi:memory-prune`.

/** TR katlanmış (`normalizeMappingLabel`) biçimde stop-list — tek başına bağlam taşımayan kelimeler. */
export const KPI_GENERIC_LABEL_WORDS: ReadonlySet<string> = new Set(
  [
    // toplam / genel
    "toplam",
    "genel",
    "genel toplam",
    "ara toplam",
    "ortalama",
    "diger",
    "digerleri",
    "tumu",
    "hepsi",
    "total",
    "other",
    "average",
    // cinsiyet / kademe / yaka
    "kadin",
    "erkek",
    "mavi",
    "beyaz",
    "yaka",
    "mavi yaka",
    "beyaz yaka",
    "female",
    "male",
    // yakıt / enerji kalemleri
    "dizel",
    "benzin",
    "motorin",
    "lpg",
    "cng",
    "lng",
    "komur",
    "elektrik",
    "dogalgaz",
    "dogal",
    "gaz",
    "su",
    "buhar",
    "yakit",
    // birim / ölçü / dönem
    "adet",
    "oran",
    "orani",
    "yuzde",
    "sayi",
    "sayisi",
    "miktar",
    "miktari",
    "deger",
    "degeri",
    "yil",
    "yillik",
    "donem",
    "ay",
    "aylik",
    "ceyrek",
    "hedef",
    "gerceklesen",
    "gerceklesme",
    "kapsam",
    "scope",
    // bağlaç / edat
    "ve",
    "veya",
    "ile",
    "icin",
    "bazinda",
    "gore",
    "olarak",
    "uzere",
    "dahil",
    "haric",
    "the",
    "of",
    "and",
    "per",
    "by",
  ].map((w) => w),
);

/** Etiket jetonları: TR katlanmış, birim/dipnot soyulmuş, harf-rakam dışı ayırıcılarla bölünmüş. */
export function mappingLabelTokens(raw: string): string[] {
  return normalizeMappingLabel(raw)
    .split(/[^a-z0-9]+/u)
    .filter((t) => t !== "");
}

/**
 * Etiket jenerik mi (P1-2)? Kural: tek jeton (ya da boş) → jenerik; birden çok jetonda hiç
 * ANLAMLI jeton yoksa → jenerik. Anlamlı jeton = stop-list dışı ve en az bir harf içeren jeton
 * (salt rakam "1" anlamlı değil).
 *   "Toplam" · "Kadın (%)" · "Dizel" · "LPG" → jenerik (tek jeton)
 *   "Mavi yaka" · "Doğal gaz" · "Kapsam 1" → jenerik (anlamlı jeton yok)
 *   "Su çekimi" · "Kadın çalışan" · "Kapsam 1 emisyonları" · "30 yaş altı" → DEĞİL
 * Jenerik etiket firma geneli hafızaya yazılmaz, oradan okunmaz.
 */
export function isGenericMappingLabel(raw: string): boolean {
  const tokens = mappingLabelTokens(raw);
  if (tokens.length <= 1) return true;
  const meaningful = tokens.filter((t) => !KPI_GENERIC_LABEL_WORDS.has(t) && /[a-z]/u.test(t));
  return meaningful.length === 0;
}

/**
 * Formül ifadesinde `oldCode` jetonunu `newCode` ile değiştirir — yalnız tam tanımlayıcı eşleşir
 * (`a` → `a_b` içinde değişmez), çift tırnaklı dizelere (CEVIR birimleri) dokunulmaz.
 * Gösterge kodu yeniden adlandırma (2026-10-08) katalog + set formüllerini bununla yeniden yazar.
 */
export function renameCodeInExpression(
  expression: string,
  oldCode: string,
  newCode: string,
): { expression: string; count: number } {
  if (oldCode === newCode || oldCode === "") return { expression, count: 0 };
  let count = 0;
  const parts = expression.split(/("[^"]*")/g);
  const out = parts.map((p, i) => {
    if (i % 2 === 1) return p; // tırnak içi
    return p.replace(/[A-Za-z_][A-Za-z0-9_]*/g, (tok) => {
      if (tok !== oldCode) return tok;
      count += 1;
      return newCode;
    });
  });
  return { expression: out.join(""), count };
}
